import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { parseExcelFile } from '../../utils/excelParser';
import { formatMoneyExacto } from '../../utils/formato';
import { useToast } from '../../components/Toast/ToastProvider';
import './ImportadorOrdenes.css';

// Empresas destino del importador. CIMSA queda afuera: es el origen de los
// centros de costo, no un destino. El backend valida la misma lista.
const EMPRESAS = ['CODYLSA', 'DYLASA', 'TDU', 'MALARGUE'];

// Espejo de la lista de back/sapAdjuntos.js. Está duplicada a propósito: acá
// sirve para filtrar el diálogo de archivos, pero quien decide es el backend.
const EXTENSIONES_ADJUNTO = '.pdf,.xlsx,.xls,.xlsm,.csv,.doc,.docx,.txt,.xml,.jpg,.jpeg,.png,.tif,.tiff,.msg,.eml';
const MAX_MB_ADJUNTO = 10;

/** Lee un archivo y lo devuelve en base64, listo para el POST. */
const leerArchivoBase64 = (archivo) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({
        nombre: archivo.name,
        // readAsDataURL devuelve "data:<mime>;base64,<datos>": mandamos sólo los datos.
        contenido: String(reader.result).split(',')[1] || ''
    });
    reader.onerror = () => reject(new Error(`No se pudo leer el archivo "${archivo.name}".`));
    reader.readAsDataURL(archivo);
});

/**
 * Réplica EXACTA de limpiarPrecio() de back/route/compras.js. El total del modal
 * tiene que ser el que SAP va a recibir, así que las dos funciones se tocan
 * juntas o no se tocan.
 *
 * La ambigüedad es el punto sin coma: en es-AR "1.234" son mil doscientos
 * treinta y cuatro, pero "15000.5" es un decimal en formato inglés. Se
 * distinguen por la forma: sólo se tratan como separador de miles los puntos
 * que agrupan de a tres dígitos exactos.
 */
const limpiarPrecio = (valor) => {
    if (typeof valor === 'number') return valor;

    let texto = String(valor ?? '').trim().replace(/\s/g, '');
    if (!texto) return NaN;

    if (texto.includes(',')) {
        texto = texto.replace(/\./g, '').replace(',', '.');
    } else if (/^-?\d{1,3}(\.\d{3})+$/.test(texto)) {
        texto = texto.replace(/\./g, '');
    }

    return parseFloat(texto);
};

const formatearTamano = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function ImportadorOrdenes() {
    const { showToast } = useToast();
    // --- ESTADOS ---
    const [selectedFile, setSelectedFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [extractedData, setExtractedData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Configuración Header
    const [empresaOrigen, setEmpresaOrigen] = useState('');
    const [textoLibre, setTextoLibre] = useState('');
    const [numeroFactura, setNumeroFactura] = useState('');

    // Adjuntos que se cuelgan de la orden en SAP (la planilla y la factura).
    const [adjuntos, setAdjuntos] = useState([]);

    // Resultado de /validar-cc: null mientras no se haya validado nada.
    const [validacion, setValidacion] = useState(null);
    const [validando, setValidando] = useState(false);

    // Proveedor al que se imputa la orden. Se lee de SAP al abrir el modal.
    const [proveedor, setProveedor] = useState(null);
    const [cargandoProveedor, setCargandoProveedor] = useState(false);

    // Credenciales SAP
    const [sapUser, setSapUser] = useState('');
    const [sapPassword, setSapPassword] = useState('');

    const comentarioPreview = ['Intercompany Web -', textoLibre.trim(), numeroFactura.trim()]
        .filter(Boolean).join(' ');

    // Importe antes de impuestos: cantidad x precio unitario de cada línea, con
    // los mismos números que se le van a mandar a SAP. El IVA lo calcula SAP a
    // partir del VatGroup de cada línea, así que acá no se suma.
    const totales = useMemo(() => {
        let neto = 0;
        let invalidas = 0;

        extractedData.forEach((fila) => {
            const cantidad = parseFloat(fila.Cantidad);
            const precio = limpiarPrecio(fila.Importe);
            // Una cantidad o un precio ilegibles harían fallar el POST en SAP:
            // conviene verlos acá y no en el mensaje de error del Service Layer.
            if (!Number.isFinite(cantidad) || !Number.isFinite(precio)) { invalidas++; return; }
            neto += cantidad * precio;
        });

        return { neto, invalidas };
    }, [extractedData]);

    // --- ADJUNTOS ---
    // Se identifican por nombre + tamaño: alcanza para no cargar dos veces el
    // mismo archivo sin impedir adjuntar dos facturas distintas.
    const agregarAdjuntos = (archivos) => {
        const nuevos = [];
        const descartados = [];

        archivos.forEach((archivo) => {
            if (archivo.size > MAX_MB_ADJUNTO * 1024 * 1024) {
                descartados.push(`${archivo.name} (supera ${MAX_MB_ADJUNTO} MB)`);
                return;
            }
            nuevos.push(archivo);
        });

        if (descartados.length) {
            showToast(`No se adjuntaron: ${descartados.join(', ')}`, 'warning', 6000);
        }

        setAdjuntos((previos) => {
            const yaEstan = new Set(previos.map(a => `${a.name}|${a.size}`));
            return [...previos, ...nuevos.filter(a => !yaEstan.has(`${a.name}|${a.size}`))];
        });
    };

    const handleAdjuntosChange = (event) => {
        agregarAdjuntos(Array.from(event.target.files || []));
        // Se limpia el input para poder volver a elegir el mismo archivo si se
        // lo quitó de la lista por error.
        event.target.value = null;
    };

    const quitarAdjunto = (indice) => setAdjuntos(prev => prev.filter((_, i) => i !== indice));

    // --- VALIDACIÓN DE EQUIVALENCIAS ---
    // Corre sola al cargar la planilla y también desde el botón, por si mientras
    // tanto se dieron de alta las equivalencias que faltaban.
    const validarEquivalencias = async (filas, empresa, silencioso = false) => {
        if (!filas.length || !empresa) return null;

        setValidando(true);
        try {
            const { data } = await api.post('/compras/validar-cc', {
                empresa,
                centrosCosto: filas.map(f => f.CentroCosto)
            });
            setValidacion(data);
            if (!silencioso) {
                showToast(
                    data.ok
                        ? `Las ${data.total} líneas tienen su equivalencia cargada.`
                        : `Hay ${data.faltantes.length} centro(s) de costo sin equivalencia.`,
                    data.ok ? 'success' : 'warning'
                );
            }
            return data;
        } catch (error) {
            showToast(`No se pudo validar: ${error.response?.data?.error || error.message}`, 'danger');
            setValidacion(null);
            return null;
        } finally {
            setValidando(false);
        }
    };

    // --- MANEJO DE ARCHIVO ---
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) setSelectedFile(file);
    };

    const handleProcessExcel = async (event) => {
        event.preventDefault();

        // Validaciones
        if (!empresaOrigen) { showToast('Selecciona la Empresa.', 'warning'); return; }
        if (!textoLibre) { showToast('Ingresa el Texto Libre.', 'warning'); return; }
        if (!numeroFactura) { showToast('Ingresa el Número de Factura.', 'warning'); return; }
        if (!selectedFile) { showToast('Selecciona un archivo Excel.', 'warning'); return; }

        setIsLoading(true);
        try {
            const json = await parseExcelFile(selectedFile);

            // --- MAPEO DE COLUMNAS ---
            const mappedData = json.map((row, index) => ({
                id: index + 1,

                // 1. Datos Principales
                TipoOM:      row['TIPO OM'] || row['Tipo OM'] || '',
                CentroCosto: row['CC'] || row['Centro Costo'] || '',
                Articulo:    row['Número de artículo'] || row['Numero de articulo'] || row['ItemCode'] || row['Articulo'] || '',

                // 2. Periodo (desde Excel)
                Periodo:     row['Periodo'] || row['Mes'] || '',

                // 3. Importes
                Cantidad:    row['Cantidad'] || 1,
                Importe:     row['Precio'] || row['Precio por unidad'] || 0,

                // 4. IVA
                IVA:         row['Indicador de impuestos'] || row['IVA'] || row['Iva'] || 21,

                // 5. Otros (El texto libre viene del input, no del excel)
                Numero:      row['Numero'] || ''
            }));

            if (mappedData.length === 0) {
                showToast('El archivo está vacío o no tiene las columnas correctas.', 'warning');
            } else {
                setExtractedData(mappedData);
                // La planilla que se acaba de leer es uno de los adjuntos de la
                // orden, así que se suma sola. Se puede quitar de la lista.
                agregarAdjuntos([selectedFile]);
                await validarEquivalencias(mappedData, empresaOrigen, true);
            }
        } catch (error) {
            showToast(error.message, 'danger');
        } finally {
            setIsLoading(false);
        }
    };

    // Abre el modal y pide el proveedor. Si SAP no contesta el modal igual se
    // muestra: quien resuelve el NumAtCard es el backend, esto es informativo.
    const abrirConfirmacion = async () => {
        setIsModalOpen(true);
        setCargandoProveedor(true);
        try {
            const { data } = await api.get('/compras/proveedor-intercompany', {
                params: { empresa: empresaOrigen }
            });
            setProveedor(data);
        } catch (error) {
            setProveedor(null);
        } finally {
            setCargandoProveedor(false);
        }
    };

    const handleCreateOrder = async () => {
        if (!sapUser || !sapPassword) {
            showToast('Ingresa tu Usuario y Contraseña de SAP.', 'warning');
            return;
        }

        setIsLoading(true);
        try {
            const adjuntosCodificados = await Promise.all(adjuntos.map(leerArchivoBase64));

            const { data: result } = await api.post('/compras/importar-oc', {
                config: {
                    empresa: empresaOrigen,
                    textoLibre: textoLibre,
                    numeroFactura: numeroFactura,
                    almacen: '01'
                },
                items: extractedData,
                adjuntos: adjuntosCodificados,
                auth: { username: sapUser, password: sapPassword }
            });

            showToast(
                `Orden creada con éxito. DocNum: ${result.DocNum} · Líneas: ${result.lineasProcesadas}` +
                (result.adjuntos ? ` · Adjuntos: ${result.adjuntos}` : ''),
                'success',
                7000
            );

            // Limpieza
            setExtractedData([]);
            setSelectedFile(null);
            setAdjuntos([]);
            setValidacion(null);
            setNumeroFactura('');
            setSapPassword('');
            setIsModalOpen(false);
            const fileInput = document.getElementById('file-upload-oc');
            if(fileInput) fileInput.value = null;

        } catch (error) {
            showToast(`Error: ${error.response?.data?.error || error.message}`, 'danger', 10000);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteRow = (id) => {
        const restantes = extractedData.filter(r => r.id !== id);
        setExtractedData(restantes);
        // Borrar la última línea de un CC sin equivalencia destraba la carga:
        // se revalida para que la tarjeta refleje lo que quedó.
        if (validacion) validarEquivalencias(restantes, empresaOrigen, true);
    };

    const handleClearData = () => {
        setExtractedData([]);
        setSelectedFile(null);
        setAdjuntos([]);
        setValidacion(null);
        const fileInput = document.getElementById('file-upload-oc');
        if(fileInput) fileInput.value = null;
    };

    const hayFaltantes = !!validacion && !validacion.ok;

    return (
        <div className="container-fluid importer-main p-4">
            <h1 className="mb-4">Importador de Órdenes</h1>

            <main>
                {/* --- TARJETA DE VALIDACIÓN DE CENTROS DE COSTO --- */}
                {validacion && (
                    <section className={`card p-3 mb-4 shadow-sm border-start border-4 ${hayFaltantes ? 'border-warning' : 'border-success'}`}>
                        {hayFaltantes ? (
                            <>
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                    <div>
                                        <h5 className="m-0 text-warning-emphasis">
                                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                            Faltan equivalencias de centro de costo
                                        </h5>
                                        <div className="text-muted small mt-1">
                                            Estos CC de CIMSA no tienen equivalencia cargada para <strong>{validacion.empresa}</strong>.
                                            La orden no se puede crear hasta resolverlos.
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <Link to="/compras/centros-costo" className="btn btn-sm btn-outline-primary">
                                            <i className="bi bi-diagram-3 me-1"></i>
                                            Cargar equivalencias
                                        </Link>
                                        <button
                                            className="btn btn-sm btn-warning"
                                            onClick={() => validarEquivalencias(extractedData, empresaOrigen)}
                                            disabled={validando}
                                        >
                                            {validando ? 'Validando…' : 'Revalidar'}
                                        </button>
                                    </div>
                                </div>

                                <div className="d-flex flex-wrap gap-2 mt-3">
                                    {validacion.faltantes.map((f) => (
                                        <span key={f.cc} className="badge bg-warning-subtle text-warning-emphasis border border-warning fs-6 fw-normal">
                                            <strong>{f.cc}</strong>
                                            <span className="ms-2 opacity-75">
                                                {f.lineas} {f.lineas === 1 ? 'línea' : 'líneas'}
                                            </span>
                                        </span>
                                    ))}
                                </div>

                                {validacion.sinCC > 0 && (
                                    <div className="alert alert-danger py-2 px-3 mt-3 mb-0 small">
                                        Además hay <strong>{validacion.sinCC}</strong> línea(s) con la columna CC vacía en el Excel.
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="text-success-emphasis">
                                    <i className="bi bi-check-circle-fill me-2"></i>
                                    Las <strong>{validacion.total}</strong> líneas tienen su equivalencia cargada para {validacion.empresa}.
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => validarEquivalencias(extractedData, empresaOrigen)}
                                    disabled={validando}
                                >
                                    {validando ? 'Validando…' : 'Revalidar'}
                                </button>
                            </div>
                        )}
                    </section>
                )}

                {/* --- SECCIÓN DE CARGA --- */}
                <section className="card p-4 mb-4 shadow-sm">
                    <form onSubmit={handleProcessExcel}>
                        <div className="card-title h5 mb-3 border-bottom pb-2">Configuración</div>

                        {/* FILA 1: Empresa, Texto Libre y Nº de Factura */}
                        <div className="row g-3 mb-3">
                            <div className="col-md-3">
                                <label className="form-label fw-bold">Empresa</label>
                                <select
                                    className="form-select"
                                    value={empresaOrigen}
                                    onChange={(e) => {
                                        setEmpresaOrigen(e.target.value);
                                        // Las equivalencias son por empresa: lo validado
                                        // para una no dice nada de la otra.
                                        setValidacion(null);
                                    }}
                                    required
                                >
                                    <option value="">-- Seleccionar --</option>
                                    {EMPRESAS.map(emp => (
                                        <option key={emp} value={emp}>{emp}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-5">
                                <label className="form-label fw-bold">Texto Libre (Referencia)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ej: 2Q JULIO 2026"
                                    value={textoLibre}
                                    onChange={(e) => setTextoLibre(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-bold">Número de Factura</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ej: FCA 0001-00000497"
                                    value={numeroFactura}
                                    onChange={(e) => setNumeroFactura(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        {/* Vista previa del comentario que va a quedar en la orden */}
                        <div className="alert alert-light border py-2 px-3 mb-4 small">
                            <span className="text-muted me-2">Comentario de la orden:</span>
                            <code className="text-dark">{comentarioPreview}</code>
                        </div>

                        {/* FILA 2: INPUT DE ARCHIVO */}
                        <div className="row g-3 align-items-end border-top pt-3 bg-light p-3 rounded">
                            <div className="col-md-10">
                                <label htmlFor="file-upload-oc" className="form-label fw-bold">Archivo Excel</label>
                                <input
                                    id="file-upload-oc"
                                    type="file"
                                    className="form-control"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileChange}
                                    required
                                />
                                <div className="form-text">
                                    Columnas: TIPO OM, CC, Número de artículo, Precio, Periodo, Indicador de impuestos
                                </div>
                            </div>
                            <div className="col-md-2">
                                <button type="submit" className="btn btn-primary w-100" disabled={isLoading || !selectedFile}>
                                    Cargar Tabla
                                </button>
                            </div>
                        </div>
                    </form>
                </section>

                {/* --- ADJUNTOS DE LA ORDEN --- */}
                <section className="card p-4 mb-4 shadow-sm">
                    <div className="card-title h5 mb-3 border-bottom pb-2">
                        Adjuntos de la orden
                        {adjuntos.length > 0 && (
                            <span className="badge bg-secondary ms-2">{adjuntos.length}</span>
                        )}
                    </div>

                    <div className="adjuntos-input mb-3">
                        <label htmlFor="file-adjuntos-oc" className="form-label fw-bold">Agregar archivos</label>
                        <input
                            id="file-adjuntos-oc"
                            type="file"
                            multiple
                            className="form-control"
                            accept={EXTENSIONES_ADJUNTO}
                            onChange={handleAdjuntosChange}
                        />
                        <div className="form-text">
                            Se cuelgan del documento en SAP. La planilla que se carga arriba se agrega sola;
                            sumá acá la factura y lo que haga falta (hasta {MAX_MB_ADJUNTO} MB por archivo).
                        </div>
                    </div>

                    {adjuntos.length === 0 ? (
                        <div className="text-muted small fst-italic">Todavía no hay archivos adjuntos.</div>
                    ) : (
                        <ul className="list-group">
                            {adjuntos.map((archivo, indice) => (
                                <li key={`${archivo.name}-${archivo.size}`} className="list-group-item d-flex justify-content-between align-items-center">
                                    <span className="text-truncate">
                                        <i className="bi bi-paperclip me-2 text-secondary"></i>
                                        {archivo.name}
                                        <span className="text-muted small ms-2">({formatearTamano(archivo.size)})</span>
                                    </span>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger py-0"
                                        onClick={() => quitarAdjunto(indice)}
                                        title="Quitar adjunto"
                                    >
                                        &times;
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                {/* --- TABLA DE PREVISUALIZACIÓN --- */}
                {extractedData.length > 0 && (
                    <section className="card p-3 shadow-sm">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="m-0">Vista Previa ({extractedData.length} líneas)</h5>
                            <div>
                                <button
                                    className="btn btn-outline-primary me-2"
                                    onClick={() => validarEquivalencias(extractedData, empresaOrigen)}
                                    disabled={validando}
                                >
                                    <i className="bi bi-check2-square me-1"></i>
                                    {validando ? 'Validando…' : 'Validar equivalencias'}
                                </button>
                                <button
                                    className="btn btn-success me-2"
                                    onClick={abrirConfirmacion}
                                    disabled={hayFaltantes}
                                    title={hayFaltantes ? 'Resolvé primero los centros de costo sin equivalencia.' : undefined}
                                >
                                    Confirmar y Crear en SAP
                                </button>
                                <button className="btn btn-outline-danger" onClick={handleClearData}>
                                    Borrar Todo
                                </button>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-sm table-striped table-bordered text-center align-middle" style={{fontSize: '0.85rem'}}>
                                <thead className="table-dark">
                                    <tr>
                                        <th>#</th>
                                        <th>TIPO OM</th>
                                        <th>CC</th>
                                        <th>Art./Item</th>
                                        <th>Periodo</th>
                                        <th>Importe</th>
                                        <th>IVA</th>
                                        <th>Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {extractedData.slice(0, 50).map((row) => {
                                        // Se marca la fila cuyo CC está en la lista de faltantes,
                                        // para poder ubicarla sin buscarla a ojo en la planilla.
                                        const ccSinEquivalencia = hayFaltantes && validacion.faltantes
                                            .some(f => f.cc.toUpperCase() === String(row.CentroCosto || '').trim().toUpperCase());

                                        return (
                                            <tr key={row.id} className={ccSinEquivalencia ? 'table-warning' : undefined}>
                                                <td>{row.id}</td>
                                                <td>{row.TipoOM}</td>
                                                <td className={ccSinEquivalencia ? 'fw-bold text-warning-emphasis' : undefined}>
                                                    {ccSinEquivalencia && <i className="bi bi-exclamation-triangle-fill me-1"></i>}
                                                    {row.CentroCosto}
                                                </td>
                                                <td className="text-primary fw-bold">{row.Articulo}</td>
                                                <td className="bg-info-subtle fw-bold">{row.Periodo}</td>
                                                <td className="text-end">{row.Importe}</td>
                                                <td>{row.IVA}</td>
                                                <td>
                                                    <button className="btn btn-sm btn-danger py-0" onClick={() => handleDeleteRow(row.id)}>&times;</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {extractedData.length > 50 && <div className="text-muted text-center mt-2">... y más líneas.</div>}
                        </div>
                    </section>
                )}
            </main>

            {/* --- MODAL DE CREDENCIALES --- */}
            {isModalOpen && (
                <div className="modal-backdrop" style={{
                    position:'fixed', top:0, left:0, width:'100%', height:'100%',
                    background:'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex: 1050
                }}>
                    <div className="bg-white p-4 rounded shadow-lg" style={{width: '460px', maxHeight: '90vh', overflowY: 'auto'}}>
                        <h5 className="mb-3">Confirmar importación</h5>

                        {/* Resumen de lo que se va a crear: el último control antes
                            de escribir en SAP. */}
                        <div className="border rounded mb-3">
                            <div className="bg-light border-bottom px-3 py-2">
                                <div className="text-muted small text-uppercase" style={{letterSpacing: '.03em'}}>
                                    Proveedor a impactar
                                </div>
                                {cargandoProveedor ? (
                                    <div className="text-muted fst-italic">Leyendo el maestro…</div>
                                ) : proveedor ? (
                                    <div>
                                        <strong>{proveedor.cardName}</strong>
                                        <span className="badge bg-secondary ms-2 fw-normal">{proveedor.cardCode}</span>
                                    </div>
                                ) : (
                                    <div className="text-warning-emphasis small">
                                        <i className="bi bi-exclamation-triangle-fill me-1"></i>
                                        No se pudo leer el nombre en SAP. La orden se crea igual con el proveedor de intercompany.
                                    </div>
                                )}
                            </div>

                            <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
                                <div>
                                    <div className="text-muted small text-uppercase" style={{letterSpacing: '.03em'}}>
                                        Importe antes de impuestos
                                    </div>
                                    <div className="text-muted" style={{fontSize: '.75rem'}}>
                                        El IVA lo calcula SAP por línea
                                    </div>
                                </div>
                                <div className="fs-4 fw-bold text-nowrap ms-3">{formatMoneyExacto(totales.neto)}</div>
                            </div>

                            <div className="px-3 py-2 small">
                                <div><span className="text-muted">Empresa:</span> <strong>{empresaOrigen}</strong></div>
                                <div><span className="text-muted">Líneas:</span> <strong>{extractedData.length}</strong></div>
                                <div><span className="text-muted">Adjuntos:</span> <strong>{adjuntos.length}</strong></div>
                                <div className="text-truncate"><span className="text-muted">Comentario:</span> {comentarioPreview}</div>
                            </div>
                        </div>

                        {totales.invalidas > 0 && (
                            <div className="alert alert-danger py-2 px-3 small">
                                <strong>{totales.invalidas}</strong> línea(s) tienen la cantidad o el precio ilegibles y
                                quedaron fuera de este total. SAP va a rechazar el documento: revisá el Excel antes de continuar.
                            </div>
                        )}

                        <div className="fw-bold border-bottom pb-1 mb-2 small text-uppercase text-muted" style={{letterSpacing: '.03em'}}>
                            Autenticación SAP
                        </div>

                        <div className="mb-3">
                            <label className="fw-bold">Usuario:</label>
                            <input type="text" className="form-control" value={sapUser} onChange={(e) => setSapUser(e.target.value)} />
                        </div>
                        <div className="mb-4">
                            <label className="fw-bold">Password:</label>
                            <input type="password" className="form-control" value={sapPassword} onChange={(e) => setSapPassword(e.target.value)} />
                        </div>
                        <div className="d-flex justify-content-end gap-2">
                            <button className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                            <button className="btn btn-success fw-bold" onClick={handleCreateOrder} disabled={isLoading}>
                                {isLoading ? 'Procesando...' : 'CONFIRMAR'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ImportadorOrdenes;
